/** Dialog for editing some VJT properties. */

import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, Input, InputLabel, MenuItem, Select } from "@material-ui/core";
import { ChangeEvent, useEffect, useState } from "react";
import { Alert, AlertTitle } from "@material-ui/lab";
import { Cancel, Save } from "@material-ui/icons";
import { objectToFormData } from "../../../../web-services/utils/object-to-form-data";
import { IVjtEditDialogProps } from "./props";
import "./vjt-edit-dialog.scss";

// eslint-disable-next-line @typescript-eslint/naming-convention
export function VjtEditDialog(props: IVjtEditDialogProps)
{
    const [errorMsg, setErrorMsg] = useState<string | undefined>();
    const [errorCategoryMsg, setErrorCategoryMessage] = useState<string | undefined>();
    const [isRequestSending, setIsRequestSending] = useState<boolean | undefined>();

    const [id, setId] = useState(props.vjt?.id);
    const [megnevezes, setMegnevezes] = useState(props.vjt?.megnevezes);
    const [kiirasTipus, setKiirasTipus] = useState(props.vjt?.kiirasTipus);
    const [statikusSzoveg, setStatikusSzoveg] = useState(props.vjt?.statikusSzoveg);
    const [parkingLotId, setParkingLotId] = useState(props.vjt?.parkingLotId);
    const [portalIp, setPortalIp] = useState(props.vjt?.portalIp);
    const [parkingLots, setParkingLots] = useState(props.parkingLots);
    const [sablonId, setSablonId] = useState(props.vjt?.sablonId);
    const [sablonok, setSablons] = useState(props.sablonok);

    useEffect(() =>
    {
        setId(props.vjt?.id);
        setMegnevezes(props.vjt?.megnevezes);
        setKiirasTipus(props.vjt?.kiirasTipus);
        setStatikusSzoveg(props.vjt?.statikusSzoveg);
        setParkingLotId(props.vjt?.parkingLotId);
        setParkingLots(props.parkingLots);
        setPortalIp(props.vjt?.portalIp);
        setSablons(props.sablonok);
        setSablonId(props.vjt?.sablonId)

        if (!props.vjt) setErrorMsg("Nincs kijelölve VJT szerkesztésre. Kérjük, nyissa meg újra a dialógust.");
        else setErrorMsg(undefined);
    }, [props.vjt, props.parkingLots]);
    useEffect(() => {
        if (kiirasTipus !== undefined) {
            if (kiirasTipus !== 0) { // Change the condition as needed
                setSablonId(filteredSablon(kiirasTipus)[0].id);
            } else
                setSablonId(undefined);
        }
    }, [kiirasTipus]);

    useEffect(() =>
    {
        if (!isRequestSending) return;

        const fetchData = async () =>
        {
            setErrorMsg(undefined);
            try
            {
                await props.httpClient.post(
                    "vjt/post-vjt-data",
                    "",
                    objectToFormData({
                        id: id,
                        kiirasTipus: kiirasTipus,
                        parkingLotId: parkingLotId || -1,
                        statikusSzoveg: statikusSzoveg,
                        sablonId: sablonId
                    }),
                    15000
                );
                setIsRequestSending(false);
                props.onSaveSuccess();
            }
            catch (err: any)
            {
                setIsRequestSending(false);
                console.log(err);

                if (err.message === "Nem várt hiba történt.")
                {
                    setErrorMsg("Kérjük, próbálja újra a mentést.");
                }
                else
                {
                    setErrorMsg(err.message);
                }
            }
        };

        fetchData().catch(console.error);
    }, [isRequestSending]);

    const renderAlert = (errorText: string | undefined, errorCategoryText: string | undefined) => (
        errorText
            ? (
                <Grid item xs={12}>
                    <Alert severity="error" className="vjt-edit-error">{errorCategoryText
                        ? <AlertTitle>{errorCategoryText}</AlertTitle> : undefined}{errorText}
                    </Alert>
                </Grid>
            )
            : undefined
    );

    const renderCircularProgress = () => (
        isRequestSending
            ? <Grid item xs={12}><div className="circular-progress-container"><CircularProgress size={24} /></div></Grid>
            : undefined
    );

    const renderDialogActions = () =>
    {
        let buttons = [
            <Button
                key={0}
                startIcon={<Cancel />}
                variant="contained"
                color="secondary"
                onClick={props.onClose}
            >
                {id ? "Mégsem" : "Bezárás"}
            </Button>
        ];
        if (id)
        {
            buttons.push(
                <Button
                    key={1}
                    startIcon={<Save />}
                    variant="contained"
                    disabled={isRequestSending}
                    color="primary"
                    onClick={() => setIsRequestSending(true)}
                    autoFocus
                >
                    Mentés
                </Button>
            );
        }

        return buttons;
    };

    const renderParkingLots = () => parkingLots.map((lot, i) => (
        <MenuItem key={i} value={lot.parkingLotId}>{lot.parkingLotName}</MenuItem>
    ));
    const filteredSablon= (kiirasTipus: number | undefined) => {
        return kiirasTipus === 1 ? sablonok.filter(sablon => sablon.statikus)
            : sablonok.filter(sablon => !sablon.statikus);
    }
    const renderSablons = (kiirasTipus: number | undefined) => {
        return filteredSablon(kiirasTipus).map((lot, i) => (
            <MenuItem key={i} value={lot.id}>{lot.megnevezes}</MenuItem>
        ));
    };


    const renderMegnevezesAndPortalIp = () =>
    {
        let megnevezesAndPortalIpFields = [];

        if (megnevezes)
        {
            megnevezesAndPortalIpFields.push(
                <Grid item xs={12} key={0}>
                    <FormControl variant="standard" className="wide-field">
                        <InputLabel>Megnevezés</InputLabel>
                        <Input
                            disabled
                            value={megnevezes}
                        />
                    </FormControl>
                </Grid>
            );
        }

        if (portalIp)
        {
            megnevezesAndPortalIpFields.push(
                <Grid item xs={12} key={1}>
                    <FormControl variant="standard" className="wide-field">
                        <InputLabel>Portal IP</InputLabel>
                        <Input
                            disabled
                            value={portalIp}
                        />
                    </FormControl>
                </Grid>
            );
        }

        return megnevezesAndPortalIpFields;
    };

    return (
        <Dialog open={props.open || false}>
            <DialogTitle>Kijelző</DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    {renderMegnevezesAndPortalIp()}
                    <Grid item xs={12}>
                        <FormControl variant="standard" className="wide-field">
                            <InputLabel>Kiírás típus</InputLabel>
                            <Select
                                value={kiirasTipus}
                                onChange={(event: ChangeEvent<{
                                    name?: string | undefined;
                                    value: unknown;
                                }>) => setKiirasTipus(event.target.value as number)}
                                label="Kiírás típus"
                            >
                                <MenuItem value={0}>Nincs</MenuItem>
                                <MenuItem value={1}>Statikus szöveg</MenuItem>
                                <MenuItem value={2}>Parking lot szabad helyek</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl variant="standard" className="wide-field">
                            <InputLabel>Statikus szöveg</InputLabel>
                            <Input
                                disabled={kiirasTipus !== 1}
                                value={statikusSzoveg}
                                inputProps={{ maxLength: 100 }}
                                onChange={(e) => setStatikusSzoveg(e.target.value)}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl variant="standard" className="wide-field">
                            <InputLabel>Parking lot</InputLabel>
                            <Select
                                disabled={kiirasTipus !== 2}
                                value={parkingLotId}
                                onChange={(event: ChangeEvent<{
                                    name?: string | undefined;
                                    value: unknown;
                                }>) => setParkingLotId(event.target.value as number)}
                                label="Parking lot"
                            >
                                {renderParkingLots()}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl variant="standard" className="wide-field">
                                <InputLabel>Sablon</InputLabel>
                            <Select
                                disabled={kiirasTipus == 0}
                                value={ kiirasTipus === 0 || sablonId === null ? null : sablonId }
                                onChange={(event: ChangeEvent<{
                                    name?: string | undefined;
                                    value: unknown;
                                }>) => setSablonId(event.target.value as number | undefined)}
                                label="Sablonasdasdasd"
                            >
                                {renderSablons(kiirasTipus)}
                            </Select>
                        </FormControl>
                    </Grid>
                    {renderCircularProgress()}
                    {renderAlert(errorMsg, errorCategoryMsg)}
                </Grid>
            </DialogContent>
            <DialogActions>
                {renderDialogActions()}
            </DialogActions>
        </Dialog>
    );
}
